import type React from 'react';

import { Button, Logo } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { useAmbassadorsHeader } from './hooks/useAmbassadorsHeader';

/**
 * Page-local slim header for the ambassadors landing page. The route hides the default
 * site header (see routes.config.ts `frame.header: false`) in favor of this simpler logo +
 * single CTA header; the default footer is kept. It sits in normal document flow (not
 * sticky/fixed) and scrolls away with the rest of the page content.
 *
 * Height is fixed at 60px to match `useHeaderHeight()` (`#src/app/app/layout`), which the
 * page's SideNav uses to offset its fixed dot column clear of this header at the top of
 * the page.
 */
export const AmbassadorsHeader: React.FC<EmptyProps> = () => {
	const { translate } = useI18n();
	const {
		data: { logoUrl },
		actions: { handleCtaClick, handleLogoClick },
	} = useAmbassadorsHeader();

	return (
		<header className="border-stroke-weaker bg-background-base h-[60px] border-b">
			<div className="container flex h-full items-center justify-between">
				{/* href matches the site header's Logo (app/layout/components/Header/components/Logo):
				    `?redirect=false` suppresses the homepage's own redirect, so clicking the wordmark
				    lands on the homepage rather than bouncing onward. */}
				<Logo
					src={logoUrl}
					href="/?redirect=false"
					onClick={handleLogoClick}
					color="primary"
					size="small"
					ariaLabel={translate('corgi.components.logo.change_org')}
					qaAttribute="ambassadors-header-home-link"
				/>
				<Button variant="primary" size="small" onClick={handleCtaClick} data-qa="ambassadors-header-cta">
					<Translate value="corgi.ambassadors.header.cta" />
				</Button>
			</div>
		</header>
	);
};
